/**
 * Persistent layout wrapper for all authenticated routes.
 * 
 * This component is mounted ONCE after login and persists across all route navigation.
 * Realtime providers are placed here to prevent subscription churn when navigating
 * between pages like /manager/payroll and /manager/timesheets.
 * 
 * Architecture:
 * - Mounts when user is authenticated with a valid profile
 * - Contains all realtime subscription providers
 * - Uses React Router's <Outlet /> to render nested route content
 * - Does NOT unmount during navigation between authenticated routes
 */

import { Outlet, Navigate } from 'react-router-dom';
import { AlertCircle, RefreshCw, LogOut, Ban } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useRolePermissionsRealtime } from '@/hooks/permissions';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { NotificationRealtimeProvider } from '@/providers/NotificationRealtimeProvider';
import { CompanyRealtimeProvider } from '@/providers/CompanyRealtimeProvider';
import { EntitlementProvider } from '@/providers/EntitlementProvider';
import { SubscriptionGate } from '@/components/billing/SubscriptionGate';
import { ConsentGate } from '@/components/auth/ConsentGate';

export function AuthenticatedRoutesLayout() {
  const { t } = useTranslation();
  const {
    user,
    loading,
    profile,
    profileError,
    retryProfileFetch,
    signOut,
    currentCompany,
    companyError,
    retryCompanyFetch,
  } = useAuth();

  // Live-invalidate cached permissions when this user's role is edited.
  // Declared before any early return to satisfy the rules of hooks.
  useRolePermissionsRealtime();

  // Show loading state while auth is resolving
  if (loading || loading === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  // Redirect to auth if not logged in
  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Check if user needs onboarding (new invited users without complete profile)
  const needsOnboarding = user && profile && (!profile.first_name || !profile.last_name);
  if (needsOnboarding) {
    return <Navigate to="/onboarding" replace />;
  }

  // Offboarded (archived/inactive) accounts are blocked at login. The database
  // identity helpers already resolve them to no tenant and no permissions;
  // this is the user-facing half of that gate.
  if (profile && (profile.status ?? 'active') !== 'active') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-sm space-y-4">
          <Ban className="h-10 w-10 text-destructive mx-auto" />
          <h2 className="text-lg font-semibold">Account deactivated</h2>
          <p className="text-sm text-muted-foreground">
            Your access to this workspace has been removed. Please contact your administrator if
            you believe this is a mistake.
          </p>
          <Button variant="outline" onClick={() => signOut()}>
            <LogOut className="mr-2 h-4 w-4" />
            {t('common.signOut', 'Sign Out')}
          </Button>
        </div>
      </div>
    );
  }

  // Profile fetch failed — show error with retry and sign-out options
  if (user && !profile && profileError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-sm space-y-4">
          <AlertCircle className="h-10 w-10 text-destructive mx-auto" />
          <h2 className="text-lg font-semibold">Unable to load your profile</h2>
          <p className="text-sm text-muted-foreground">{profileError}</p>
          <div className="flex justify-center gap-3">
            <Button variant="outline" onClick={retryProfileFetch}>
              <RefreshCw className="mr-2 h-4 w-4" />
              {t('common.retry', 'Retry')}
            </Button>
            <Button variant="ghost" onClick={() => signOut()}>
              <LogOut className="mr-2 h-4 w-4" />
              {t('common.signOut', 'Sign Out')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // If we have a user but no profile and no error, profile fetch is in progress
  if (user && !profile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  // Company (tenant) context failed to load — block the app with a recoverable error
  if (profile && !currentCompany && companyError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-sm space-y-4">
          <AlertCircle className="h-10 w-10 text-destructive mx-auto" />
          <h2 className="text-lg font-semibold">Unable to load your workspace</h2>
          <p className="text-sm text-muted-foreground">{companyError}</p>
          <div className="flex justify-center gap-3">
            <Button variant="outline" onClick={retryCompanyFetch}>
              <RefreshCw className="mr-2 h-4 w-4" />
              {t('common.retry', 'Retry')}
            </Button>
            <Button variant="ghost" onClick={() => signOut()}>
              <LogOut className="mr-2 h-4 w-4" />
              {t('common.signOut', 'Sign Out')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Profile loaded but company fetch still in flight
  if (profile && !currentCompany && !companyError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  // User is authenticated with a valid profile - render providers and outlet
  // These providers persist across all route navigation within authenticated routes
  return (
    <ConsentGate>
      <EntitlementProvider>
        <NotificationRealtimeProvider>
          <CompanyRealtimeProvider>
            <SubscriptionGate>
              <Outlet />
            </SubscriptionGate>
          </CompanyRealtimeProvider>
        </NotificationRealtimeProvider>
      </EntitlementProvider>
    </ConsentGate>
  );
}

export default AuthenticatedRoutesLayout;
